import { MonitorStatus, monitorStatusBgCssClass } from '@fe/core';
import { CompositeFilterDescriptor, DataResult, FilterOperator, SortDescriptor, State } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Grid, GridColumn, GridCustomRowProps, GridHandle, GridNoRecords } from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import React from 'react';
import { Link } from 'react-router';
import { AirportMonitorItem } from '../../../../../airports/airport-dto';
import commonFormatter from '../../../../../formatters/common-formatter';
import { useGotoTargetMaps } from '../../../../../hooks/use-goto-target-maps';
import { SortableOutline, WarningTriangleOutline } from '../../../../../icons/icons';
import { MessageType } from '../../../../../services/actor-messages';
import { AirportMonitorSortOptions, MonitorStatusOptions, SortDirectionOptions } from '../../../../../types/dropdown-options';
import { AirportIdDisplayStyleEnum, AirportMonitorSortType, SortOrderEnum } from '../../../../../types/settings';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import CustomPopover from '../../../../components/layout/CustomPopover';
import MonitorStatusStripe from '../../../../components/monitor-status/MonitorStatusStripe';
import { OpenInMultiWindowButton } from '../../../../components/multi-window/OpenMultiWindowButton';
import { WorkspaceWindowPanelType } from '../../../../components/multi-window/types';
import { EventBus, useEvent } from '../../../../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import useMap from '../../../../hooks/use-map';
import { setSetting } from '../../../../state/slices/settings-slice';
import { PanelTitleTrailingContent } from '../HomePanel';
import './AirportMonitor.css';

export function AirportMonitor() {
    const myAirportsFilterGroup = useAppSelector((x) => x.settings.myAirportFilterGroup);
    const myAirportUseOrgDestAlt = useAppSelector((x) => x.settings.myAirportIncludeMyFleetOrgDestAlt);
    const [state, setState] = React.useState<State>({ take: 50, skip: 0 });
    const [dataResult, setDataResult] = React.useState<DataResult>({ total: 0, data: [] });
    const displayStyle = useAppSelector((x) => x.settings.airportIdDisplayStyle);
    const collapsedView = useAppSelector((x) => x.settings.airportMonitorCollapsedView);
    const sortBy = useAppSelector((x) => x.settings.defaultAirportSortType);
    const sortDir = useAppSelector((x) => x.settings.airportMonitorMonitorSortOrder);
    const minimumMonitorLevel = useAppSelector((x) => x.settings.airportMonitorMinimumLevel);
    const map = useMap();
    const { goToAirport } = useGotoTargetMaps();
    const dispatch = useAppDispatch();
    const worker = useFlightWorker();
    const grid = React.useRef<GridHandle>(null);
    const isMyAirportsDefined = myAirportUseOrgDestAlt || !!myAirportsFilterGroup;

    const loadData = React.useCallback(
        async (state: State) => {
            const result = await worker.actor.sendAsync({ type: MessageType.queryAirportMonitor, data: state });
            setState(state);
            setDataResult(result);
        },
        [worker]
    );

    const filterAndSort = React.useMemo(
        () =>
            ({
                filter: getFilter(minimumMonitorLevel),
                sort: getSort(sortBy, sortDir)
            }) satisfies Pick<State, 'filter' | 'sort'>,
        [sortBy, sortDir, minimumMonitorLevel]
    );

    // Reload when filter and sort change
    React.useEffect(() => {
        loadData({ ...state, ...filterAndSort });
    }, [loadData, filterAndSort]);

    useEvent(
        'airports-changed',
        React.useCallback(() => {
            if (!grid.current) {
                return;
            }
            loadData({
                take: grid.current.props.take,
                skip: grid.current.props.skip,
                filter: grid.current.props.filter,
                sort: grid.current.props.sort
            });
        }, [loadData])
    );

    const handleContextMenu = React.useCallback((e: MouseEvent, airport: AirportMonitorItem) => {
        EventBus.fireContextMenu([{ kind: 'airport', airportId: airport.id }], e);
    }, []);

    const handleOnClick = React.useCallback(
        (e: MouseEvent, airport: AirportMonitorItem) => {
            if (airport.position.isValid) {
                goToAirport(airport.position);
            }
        },
        [map]
    );

    const rowComponent = React.useMemo(
        () => AirportMonitorItemRow({ displayStyle, collapsedView, onContextMenu: handleContextMenu, onClick: handleOnClick }),
        [displayStyle, collapsedView, handleContextMenu, handleOnClick]
    );

    return (
        <div className="h-100 d-flex flex-column">
            <PanelTitleTrailingContent>
                <div className="d-flex">
                    <OpenInMultiWindowButton type={WorkspaceWindowPanelType.airportMonitor} />
                    <CustomPopover
                        trigger={
                            <Button
                                title={'Sort and filter options'}
                                className="button-small"
                                svgIcon={SortableOutline}
                                fillMode={'flat'}
                            />
                        }
                        offsetOptions={{
                            crossAxis: -5
                        }}
                        closeOnClick={false}>
                        <div className="p-2 position-relative d-flex flex-column gap-2" style={{ width: 300 }}>
                            <div>
                                <div>Sort by:</div>
                                <DropdownListWithValueField
                                    data={AirportMonitorSortOptions}
                                    value={sortBy}
                                    onChange={(e) => dispatch(setSetting('defaultAirportSortType', e.value))}
                                />
                            </div>
                            <div>
                                <div>Direction:</div>
                                <DropdownListWithValueField
                                    data={SortDirectionOptions}
                                    value={sortDir}
                                    onChange={(e) => dispatch(setSetting('airportMonitorMonitorSortOrder', e.value))}
                                />
                            </div>
                            <div>
                                <div>Minimum monitor level:</div>
                                <DropdownListWithValueField
                                    data={MonitorStatusOptions}
                                    value={minimumMonitorLevel}
                                    onChange={(e) => dispatch(setSetting('airportMonitorMinimumLevel', e.value))}
                                />
                            </div>
                            <Checkbox
                                label="Collapsed view"
                                checked={collapsedView}
                                onChange={(e) => dispatch(setSetting('airportMonitorCollapsedView', e.value))}
                            />
                        </div>
                    </CustomPopover>
                </div>
            </PanelTitleTrailingContent>

            {isMyAirportsDefined ? (
                <Grid
                    ref={grid}
                    className="k-grid-no-header k-grid-no-borders flex-grow-1 min-h-0"
                    data={dataResult}
                    dataItemKey="id"
                    rowHeight={collapsedView ? 25 : 35}
                    rows={{
                        groupHeader: () => <></>,
                        data: rowComponent
                    }}
                    onDataStateChange={(e) => loadData(e.dataState)}
                    scrollable="virtual"
                    {...state}>
                    <GridColumn id="main" field="df" />
                    <GridNoRecords>
                        <div></div>
                    </GridNoRecords>
                </Grid>
            ) : (
                <div className="p-2 secondary-text text-center">
                    <SvgIcon themeColor="warning" icon={WarningTriangleOutline} />
                    <span>You have not selected any airport for My Airports. You can do it </span>
                    <Link to="/settings/monitor-alert">settings</Link>
                </div>
            )}
        </div>
    );
}
export function AirportMonitorItemRow(extra: {
    displayStyle: AirportIdDisplayStyleEnum;
    collapsedView: boolean;
    onContextMenu: (e: MouseEvent, airport: AirportMonitorItem) => void;
    onClick: (e: MouseEvent, airport: AirportMonitorItem) => void;
}) {
    return function (props: GridCustomRowProps) {
        const airport = props.dataItem as AirportMonitorItem;
        return (
            <tr {...props.trProps}>
                <td
                    className="am-monitor-container d-flex gap-1 border-bottom p-1"
                    onContextMenu={(e) => {
                        extra.onContextMenu(e.nativeEvent, airport);
                        e.preventDefault();
                    }}
                    onClick={(e) => {
                        extra.onClick(e.nativeEvent, airport);
                    }}>
                    <MonitorStatusStripe monitorStatus={airport.monitorStatus} className="flex-shrink-0" />
                    <div className="flex-grow-1 flex-shrink-1 min-w-0 d-flex flex-column justify-content-center">
                        <div className="am-id fw-bold">{commonFormatter.formatAirportId(airport, extra.displayStyle)}</div>
                        {!extra.collapsedView && <div className="secondary-text-color am-name">{airport.name}</div>}
                    </div>
                    {!extra.collapsedView && (
                        <div className="am-oda d-flex flex-column justify-content-between flex-shrink-0 secondary-text-color">
                            <div className={monitorStatusBgCssClass[airport.origin.monitorStatus]}>O {airport.origin.count}</div>
                            <div className={monitorStatusBgCssClass[airport.destination.monitorStatus]}>D {airport.destination.count}</div>
                            <div className={monitorStatusBgCssClass[airport.alternate.monitorStatus]}>A {airport.alternate.count}</div>
                        </div>
                    )}
                </td>
            </tr>
        );
    };
}

function getSort(sortBy: AirportMonitorSortType, sortDir: SortOrderEnum): SortDescriptor[] {
    const dir = sortDir === SortOrderEnum.asc ? 'asc' : 'desc';
    switch (sortBy) {
        case AirportMonitorSortType.monitorStatusPriorityId:
            return [
                { field: 'thresholds.monitorStatus', dir },
                { field: 'thresholds.priority', dir },
                { field: 'recordId', dir }
            ];
        case AirportMonitorSortType.id:
            return [{ field: 'recordId', dir }];
    }
    return [];
}

function getFilter(minimumMonitorLevel: MonitorStatus): CompositeFilterDescriptor {
    const filter: CompositeFilterDescriptor = {
        logic: 'and',
        filters: []
    };

    if (minimumMonitorLevel > MonitorStatus.null) {
        filter.filters.push({
            field: 'thresholds.monitorStatus',
            operator: FilterOperator.GreaterThanOrEqual,
            value: minimumMonitorLevel
        });
    }
    return filter;
}
